import {
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  EnvironmentFilled,
  ExpandAltOutlined,
  FilterOutlined,
  SaveOutlined,
  ShrinkOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  BarometerInHg,
  BarometerTrend,
  CloudCoverPercent,
  Waypoint,
  WaypointFilter,
  WindDirection,
  WindMph,
} from '@omniafishing/core';
import { Select, Skeleton } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { XMLBuilder } from 'fast-xml-parser';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDimensions } from '../../../../hooks/use_dimensions';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useUser } from '../../../../hooks/use_user';
import { useWaypointFilters } from '../../../../hooks/use_waypoint_filters';
import { SignupLoginModalActions } from '../../../../redux/signup_login_modal';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { FiltersScreen, FiltersScreenProps, WaypointType } from './filters_screen';
import { WaypointsList } from './waypoints_list';
import styles from './waypoints_list_screen.less';
import { areWaypointDatesSame } from './waypoint_card';
import { WaypointFilterNameModal } from './waypoint_filter_name_modal';

interface GpxWaypoint {
  '@_lat': number;
  '@_lon': number;
  name: string;
  time: string;
  type: string;
  cmt?: string;
  sym?: string;
}

export const buildWaypointsXml = (waypoints: Waypoint[]) => {
  return waypoints.map((waypoint) => {
    const wptObject: GpxWaypoint = {
      '@_lat': waypoint.lat,
      '@_lon': waypoint.lng,
      name: waypoint.name,
      time: waypoint.outing_at,
      type: waypoint.waypoint_type,
    };
    if (waypoint.comments) {
      wptObject.cmt = waypoint.comments;
    }
    if (waypoint.symbol_imported) {
      wptObject.sym = waypoint.symbol_imported;
    }
    return wptObject;
  });
};

const generateWaypointXML = (waypoints: Waypoint[]) => {
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    arrayNodeName: 'wpt',
  });

  const gpxObject = {
    gpx: {
      '@_version': '1.1',
      '@_creator': 'Omnia Fishing',
      '@_xmlns': 'http://www.topografix.com/GPX/1/1',
      wpt: buildWaypointsXml(waypoints),
    },
  };

  return builder.build(gpxObject);
};

interface WaypointsListScreenProps {
  allWaypoints: Waypoint[];
  waypointsLoading: boolean;
  onClose: () => void;
  onEditableWaypointChange: (waypoint: Waypoint | null, flyTo?: boolean) => void;
  onFilterChange: FiltersScreenProps['onFilterChange'];
  onSelectedWaypointChange: (waypoint: Waypoint) => void;
  onWaypointCreate: () => void;
  onWaypointDelete: (waypointId: number) => void;
  onWaypointImportScreenChange: (val: boolean) => void;
  selectedBarometricPressures: BarometerInHg[];
  selectedBarometricPressureTrends: BarometerTrend[];
  selectedCloudCovers: CloudCoverPercent[];
  selectedEndDate: string;
  selectedStartDate: string;
  selectedWaterbodyIds: string[];
  selectedWaypointTypes: WaypointType[];
  selectedWindDirections: WindDirection[];
  selectedWindSpeeds: WindMph[];
  waypointsToRender: Waypoint[];
}

export const WaypointsListScreen = React.memo((props: WaypointsListScreenProps) => {
  const {
    allWaypoints,
    waypointsLoading,
    onClose,
    onEditableWaypointChange,
    onFilterChange,
    onSelectedWaypointChange,
    onWaypointCreate,
    onWaypointDelete,
    onWaypointImportScreenChange,
    selectedBarometricPressures,
    selectedBarometricPressureTrends,
    selectedCloudCovers,
    selectedEndDate,
    selectedStartDate,
    selectedWaterbodyIds,
    selectedWaypointTypes,
    selectedWindDirections,
    selectedWindSpeeds,
    waypointsToRender,
  } = props;
  const { user, isPro } = useUser();
  const dispatch = useDispatch();
  const { isMobile, isDesktop } = useResponsive();
  const [sort, setSort] = useState(0); // sort by new is 0
  const [hasInteractedSort, setHasInteractedSort] = useState(false); // State to track interaction
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [savedFilterApplied, setSavedFilterApplied] = useState<WaypointFilter>();
  const { updateWaypointFilter } = useWaypointFilters();

  const [showFiltersScreen, setShowFiltersScreen] = useState(false);

  const hasSelectedWaterbodyIds = selectedWaterbodyIds.length > 0;
  const hasSelectedWaypointTypes = selectedWaypointTypes.length > 0;
  const hasSelectedWaypointDateRange = selectedStartDate != null && selectedEndDate != null;
  const hasSelectedBarometricPressures = selectedBarometricPressures.length > 0;
  const hasSelectedBarometricPressureTrends = selectedBarometricPressureTrends.length > 0;
  const hasSelectedCloudCovers = selectedCloudCovers.length > 0;
  const hasSelectedWindDirections = selectedWindDirections.length > 0;
  const hasSelectedWindSpeeds = selectedWindSpeeds.length > 0;

  const hasFilters =
    hasSelectedWaterbodyIds ||
    hasSelectedWaypointTypes ||
    hasSelectedWaypointDateRange ||
    hasSelectedBarometricPressures ||
    hasSelectedBarometricPressureTrends ||
    hasSelectedCloudCovers ||
    hasSelectedWindDirections ||
    hasSelectedWindSpeeds;

  const handleClearFilters = useCallback(() => {
    setSavedFilterApplied(undefined);
    onFilterChange({
      selectedWaterbodyIds: [],
      selectedWaypointTypes: [],
      selectedEndDate: null,
      selectedStartDate: null,
      selectedWindDirections: [],
      selectedWindSpeeds: [],
      selectedCloudCovers: [],
      selectedBarometricPressures: [],
      selectedBarometricPressureTrends: [],
    });
  }, []);

  const showSortFilterButtons = allWaypoints.length > 1;

  const subheaderHeight = isDesktop ? 111 : 107;

  const waypointsSorted = useMemo(() => {
    return _.orderBy(
      waypointsToRender,
      (w) => (areWaypointDatesSame(w) ? w.created_at : w.outing_at),
      sort === 0 ? 'desc' : 'asc'
    );
  }, [sort, waypointsToRender]);

  const handleSortChange = useCallback((val: number) => {
    setHasInteractedSort(true);
    setSort(val);
  }, []);

  const handleWaypointExport = useCallback(() => {
    const xml = generateWaypointXML(waypointsSorted);
    const blob = new Blob([xml], { type: 'text/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omnia_fishing_waypoints_${dayjs().format('MM_DD_YYYY_HH_mm_ss')}.gpx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [waypointsSorted]);

  const [mobileExpandedModal, setMobileExpandedModal] = useState(false);

  const [measureRef, bounds] = useDimensions({
    onResize: true,
    onElementResize: true,
  });
  const boundsHeight = bounds?.height || 0;

  const onSaveFilter = () => {
    setSaveFilterModalOpen(true);
  };

  const onUpdateSavedFilter = () => {
    updateWaypointFilter.mutate(
      {
        id: savedFilterApplied.id,
        updateParams: {
          barometer_in_hgs: selectedBarometricPressures,
          barometer_trends: selectedBarometricPressureTrends,
          cloud_cover_percents: selectedCloudCovers,
          end_date: selectedEndDate,
          name: savedFilterApplied.name,
          start_date: selectedStartDate,
          waterbodies: selectedWaterbodyIds,
          waypoint_types: selectedWaypointTypes,
          wind_directions: selectedWindDirections,
          wind_mphs: selectedWindSpeeds,
        },
      },
      {
        onSuccess: (waypointFilter) => {
          setSavedFilterApplied(waypointFilter);
        },
      }
    );
  };

  const onSavedFilterApplied = (filter: WaypointFilter) => {
    setSavedFilterApplied(filter);
  };

  const savedFilterDiffersFromFilters = useMemo(() => {
    if (!savedFilterApplied) {
      return false;
    }
    type FilterComparison = Pick<
      WaypointFilter,
      | 'barometer_in_hgs'
      | 'barometer_trends'
      | 'cloud_cover_percents'
      | 'end_date'
      | 'start_date'
      | 'waypoint_types'
      | 'wind_directions'
      | 'wind_mphs'
    > & { waterbodies: string[] };

    const savedFilter: FilterComparison = {
      barometer_in_hgs: savedFilterApplied.barometer_in_hgs,
      barometer_trends: savedFilterApplied.barometer_trends,
      cloud_cover_percents: savedFilterApplied.cloud_cover_percents,
      end_date: savedFilterApplied.end_date,
      start_date: savedFilterApplied.start_date,
      waterbodies: savedFilterApplied.waterbodies.map((waterbody) => waterbody.id),
      waypoint_types: savedFilterApplied.waypoint_types,
      wind_directions: savedFilterApplied.wind_directions,
      wind_mphs: savedFilterApplied.wind_mphs,
    };
    const filters: FilterComparison = {
      barometer_in_hgs: selectedBarometricPressures,
      barometer_trends: selectedBarometricPressureTrends,
      cloud_cover_percents: selectedCloudCovers,
      end_date: selectedEndDate,
      start_date: selectedStartDate,
      waterbodies: selectedWaterbodyIds,
      waypoint_types: selectedWaypointTypes,
      wind_directions: selectedWindDirections,
      wind_mphs: selectedWindSpeeds,
    };

    return !_.isEqual(savedFilter, filters);
  }, [
    savedFilterApplied?.barometer_in_hgs,
    savedFilterApplied?.barometer_trends,
    savedFilterApplied?.cloud_cover_percents,
    savedFilterApplied?.end_date,
    savedFilterApplied?.start_date,
    savedFilterApplied?.waypoint_types,
    savedFilterApplied?.wind_directions,
    savedFilterApplied?.wind_mphs,
    selectedBarometricPressures,
    selectedBarometricPressureTrends,
    selectedCloudCovers,
    selectedEndDate,
    selectedStartDate,
    selectedWaterbodyIds,
    selectedWaypointTypes,
    selectedWindDirections,
    selectedWindSpeeds,
  ]);

  return (
    <div
      className={classNames(styles.modal, { [styles.modal__expanded]: mobileExpandedModal })}
      ref={measureRef}
    >
      <div className={styles.modalContent}>
        {showFiltersScreen ? (
          <FiltersScreen
            defaultSelectedBarometricPressures={selectedBarometricPressures}
            defaultSelectedBarometricPressureTrends={selectedBarometricPressureTrends}
            defaultSelectedCloudCovers={selectedCloudCovers}
            defaultSelectedEndDate={selectedEndDate}
            defaultSelectedStartDate={selectedStartDate}
            defaultSelectedWaterbodyIds={selectedWaterbodyIds}
            defaultSelectedWaypointTypes={selectedWaypointTypes}
            defaultSelectedWindDirections={selectedWindDirections}
            defaultSelectedWindSpeeds={selectedWindSpeeds}
            onClose={() => {
              setShowFiltersScreen(false);
              setMobileExpandedModal(false);
            }}
            onSavedFilterApplied={onSavedFilterApplied}
            savedFilterApplied={savedFilterApplied}
            onModalClose={onClose}
            onFilterChange={onFilterChange}
            waypoints={allWaypoints}
          />
        ) : (
          <>
            <div className={styles.modalHeader}>
              <span className={styles.heading}>Waypoints</span>
              <div>
                {isMobile && (
                  <span
                    role="button"
                    tabIndex={1}
                    className={styles.expandIcon}
                    onClick={() => setMobileExpandedModal(!mobileExpandedModal)}
                  >
                    {mobileExpandedModal ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                  </span>
                )}
                <CloseOutlined onClick={onClose} />
              </div>
            </div>
            <ul className={styles.waypointList}>
              {user ? (
                <>
                  {waypointsLoading ? (
                    <div className={styles.loading}>
                      {Array.from({ length: 2 }, (__, i) => {
                        return <Skeleton active key={i} paragraph={{ rows: 4 }} />;
                      })}
                    </div>
                  ) : (
                    <>
                      {allWaypoints.length === 0 ? (
                        <div className={styles.noWaypoints}>
                          <EnvironmentFilled />
                          <strong> No Waypoints Yet</strong>
                          <small>
                            You haven't added any waypoints yet. Create one to easily track your
                            favorite spots.
                          </small>
                          {isMobile && (
                            <OmniaButton
                              onPress={onWaypointCreate}
                              size="md"
                              kind="primary"
                              className="py-[5.5px] px-[15px] mt-3"
                            >
                              {' '}
                              Create{' '}
                            </OmniaButton>
                          )}
                        </div>
                      ) : (
                        <>
                          {showSortFilterButtons && (
                            <>
                              <div className={styles.filters}>
                                {isMobile && (
                                  <>
                                    <OmniaButton
                                      onPress={onWaypointCreate}
                                      size="md"
                                      kind="primary"
                                      className="py-[5.5px] px-[15px]"
                                    >
                                      {' '}
                                      Create{' '}
                                    </OmniaButton>
                                    {hasFilters && (
                                      <OmniaButton
                                        kind="link"
                                        size="md"
                                        onPress={handleClearFilters}
                                        className={classNames(
                                          styles.clearButton,
                                          'py-[5.5px] px-[15px]'
                                        )}
                                      >
                                        Clear
                                      </OmniaButton>
                                    )}
                                  </>
                                )}

                                <div>
                                  <OmniaButton
                                    kind="tertiary"
                                    size="md"
                                    onPress={() => {
                                      setShowFiltersScreen(true);
                                      setMobileExpandedModal(true);
                                    }}
                                    className={styles.filtersButton}
                                  >
                                    <FilterOutlined /> Filters
                                  </OmniaButton>

                                  {hasFilters && isDesktop && (
                                    <OmniaButton
                                      kind="link"
                                      size="md"
                                      onPress={handleClearFilters}
                                      className={styles.clearButton}
                                    >
                                      Clear
                                    </OmniaButton>
                                  )}
                                </div>

                                <Select
                                  placeholder="Sort"
                                  onChange={handleSortChange}
                                  className={styles.sortSelect}
                                  dropdownMatchSelectWidth={false}
                                  value={hasInteractedSort ? sort : undefined} // Conditional value setting
                                  options={[
                                    {
                                      label: 'Newest',
                                      value: 0,
                                    },
                                    {
                                      label: 'Oldest',
                                      value: 1,
                                    },
                                  ]}
                                />
                              </div>
                            </>
                          )}
                          <WaypointsList
                            carouselHeight={Math.max(boundsHeight - subheaderHeight, 0)}
                            onEditableWaypointChange={onEditableWaypointChange}
                            onSelectedWaypointChange={onSelectedWaypointChange}
                            onWaypointDelete={onWaypointDelete}
                            waypoints={waypointsSorted}
                          />
                          {isMobile && waypointsSorted.length === 1 && (
                            <div className="sticky p-3 pb-2 top-0 bg-white text-center">
                              <OmniaButton
                                onPress={onWaypointCreate}
                                size="md"
                                kind="primary"
                                className="py-[5.5px] px-[15px]"
                              >
                                {' '}
                                Create{' '}
                              </OmniaButton>
                            </div>
                          )}
                        </>
                      )}{' '}
                    </>
                  )}
                </>
              ) : (
                <div className={styles.noWaypoints}>
                  <EnvironmentFilled />
                  <span>No Waypoints Yet </span>
                  <p>
                    <OmniaButton
                      onClick={() =>
                        dispatch(
                          SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN({
                            heading: 'Create an account',
                          })
                        )
                      }
                      size="md"
                      kind="link"
                      style={{ padding: 0 }}
                    >
                      Create an Account
                    </OmniaButton>{' '}
                    or{' '}
                    <OmniaButton
                      size="md"
                      kind="link"
                      onClick={() =>
                        dispatch(
                          SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN({
                            initialForm: 'login',
                          })
                        )
                      }
                      style={{ padding: 0 }}
                    >
                      Log In
                    </OmniaButton>{' '}
                    to enable waypoints.
                  </p>
                </div>
              )}
            </ul>
            <div className={styles.footerWrapper}>
              <div className={styles.footer}>
                <div
                  className={classNames(styles.footerContent, {
                    [styles.footerContent__noUser]: !user,
                  })}
                >
                  {user ? (
                    <>
                      <div className={styles.buttonWrapper}>
                        <OmniaButton
                          onPress={onWaypointCreate}
                          kind="primary"
                          size="lg"
                          shape="circle"
                          className={styles.actionButton}
                        >
                          <EditOutlined />
                        </OmniaButton>
                        <strong>Create </strong>
                      </div>
                      <div className={styles.buttonWrapper}>
                        <OmniaButton
                          onPress={() => onWaypointImportScreenChange(true)}
                          kind="tertiary"
                          size="lg"
                          shape="circle"
                          className={styles.actionButton}
                        >
                          <DownloadOutlined />
                        </OmniaButton>
                        <strong>Import </strong>
                      </div>
                      <div className={styles.buttonWrapper}>
                        <OmniaButton
                          onPress={handleWaypointExport}
                          kind="tertiary"
                          size="lg"
                          shape="circle"
                          className={styles.actionButton}
                        >
                          <UploadOutlined />
                        </OmniaButton>
                        <strong>Export </strong>
                      </div>
                      {isPro && hasFilters && (
                        <div className={styles.buttonWrapper}>
                          {savedFilterApplied && savedFilterDiffersFromFilters ? (
                            <>
                              <OmniaButton
                                onPress={onUpdateSavedFilter}
                                kind="primary"
                                size="lg"
                                shape="circle"
                                className={styles.actionButton}
                              >
                                <SaveOutlined />
                              </OmniaButton>
                              <strong>Update Filter</strong>
                            </>
                          ) : (
                            <>
                              {!savedFilterApplied && (
                                <>
                                  <OmniaButton
                                    onPress={onSaveFilter}
                                    kind="primary"
                                    size="lg"
                                    shape="circle"
                                    className={styles.actionButton}
                                  >
                                    <SaveOutlined />
                                  </OmniaButton>
                                  <strong>Save Filter</strong>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <OmniaButton
                        onPress={() =>
                          dispatch(
                            SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN({
                              initialForm: 'login',
                            })
                          )
                        }
                        kind="tertiary"
                        size="lg"
                        block
                        style={{ marginBottom: '8px' }}
                      >
                        Log In
                      </OmniaButton>
                      <OmniaButton
                        onPress={() =>
                          dispatch(
                            SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN({
                              heading: 'Create an account',
                            })
                          )
                        }
                        kind="primary"
                        size="lg"
                        block
                      >
                        Create an Account
                      </OmniaButton>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <WaypointFilterNameModal
        open={saveFilterModalOpen}
        selectedBarometricPressures={selectedBarometricPressures}
        selectedBarometricPressureTrends={selectedBarometricPressureTrends}
        selectedCloudCovers={selectedCloudCovers}
        selectedEndDate={selectedEndDate}
        selectedStartDate={selectedStartDate}
        selectedWaterbodyIds={selectedWaterbodyIds}
        selectedWaypointTypes={selectedWaypointTypes}
        selectedWindDirections={selectedWindDirections}
        selectedWindSpeeds={selectedWindSpeeds}
        onSuccess={(waypointFilter) => {
          setSaveFilterModalOpen(false);
          setSavedFilterApplied(waypointFilter);
        }}
        onCancel={() => setSaveFilterModalOpen(false)}
      />
    </div>
  );
});
